import React, { useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Box from '@material-ui/core/Box';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Theme } from '@material-ui/core/styles';
import AssignmentIcon from '@material-ui/icons/Assignment';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import { useRouteRef, useApp } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import {
  DocsIcon,
  Page,
  Header,
  Content,
  ContentHeader,
  SupportButton,
} from '@backstage/core-components';
import {
  EntityListProvider,
  CatalogFilterLayout,
  EntitySearchBar,
  EntityKindPicker,
  UserListPicker,
  EntityTagPicker,
  EntityOwnerPicker,
} from '@backstage/plugin-catalog-react';
import {
  TemplateCategoryPicker,
  TemplateGroups,
  ScaffolderPageContextMenu,
} from '@backstage/plugin-scaffolder-react/alpha';
import { TemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';
import { TemplateListPageProps, scaffolderTranslationRef } from '@backstage/plugin-scaffolder/alpha';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { taskReadPermission } from '@backstage/plugin-scaffolder-common/alpha';
import { usePermission } from '@backstage/plugin-permission-react';
import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';

type RegisterExistingButtonProps = {
  title: string;
  to?: string;
};

const RegisterExistingButton = ({ title, to }: RegisterExistingButtonProps) => {
  const { allowed } = usePermission({
    permission: catalogEntityCreatePermission,
  });
  const isXSScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'));

  if (!to || !allowed) {
    return null;
  }

  if (isXSScreen) {
    return (
      <IconButton
        component={Link}
        color="primary"
        title={title}
        size="small"
        to={to}
      >
        <CreateComponentIcon />
      </IconButton>
    );
  }

  return (
    <Button component={Link} variant="outlined" color="primary" to={to}>
      {title}
    </Button>
  );
};

const TemplateListPageWithTasks: React.FC<TemplateListPageProps> = props => {
  const navigate = useNavigate();
  const app = useApp();
  const { t } = useTranslationRef(scaffolderTranslationRef);

  const templateRoute = useRouteRef(scaffolderPlugin.routes.selectedTemplate);
  const editorLink = useRouteRef(scaffolderPlugin.routes.edit);
  const actionsLink = useRouteRef(scaffolderPlugin.routes.actions);
  const tasksLink = useRouteRef(scaffolderPlugin.routes.listTasks);
  const viewTechDocsLink = useRouteRef(scaffolderPlugin.externalRoutes.viewTechDoc);
  const registerComponentRoute = useRouteRef(scaffolderPlugin.externalRoutes.registerComponent);

  const { TemplateCardComponent, groups: providedGroups = [], templateFilter, headerOptions } =
    props;

  const groups = providedGroups.length
    ? [
        ...providedGroups,
        {
          title: t('templateListPage.templateGroups.otherTitle'),
          filter: (entity: TemplateEntityV1beta3) =>
            !providedGroups.some(({ filter }) => filter(entity)),
        },
      ]
    : [
        {
          title: t('templateListPage.templateGroups.defaultTitle'),
          filter: () => true,
        },
      ];

  const scaffolderPageContextMenuProps = {
    onEditorClicked:
      props?.contextMenu?.editor !== false ? () => navigate(editorLink()) : undefined,
    onActionsClicked:
      props?.contextMenu?.actions !== false ? () => navigate(actionsLink()) : undefined,
    onTasksClicked:
      props?.contextMenu?.tasks !== false ? () => navigate(tasksLink()) : undefined,
  };

  const additionalLinksForEntity = useCallback(
    (template: any) => {
      const { kind, namespace, name } = parseEntityRef(stringifyEntityRef(template));
      if (
        !viewTechDocsLink ||
        !template.metadata.annotations?.['backstage.io/techdocs-ref']
      ) {
        return [];
      }
      return [
        {
          icon: app.getSystemIcon('docs') ?? DocsIcon,
          text: t('templateListPage.additionalLinksForEntity.viewTechDocsTitle'),
          url: viewTechDocsLink({ kind, namespace, name }),
        },
      ];
    },
    [app, viewTechDocsLink, t],
  );

  const onTemplateSelected = useCallback(
    (template: any) => {
      const { namespace, name } = parseEntityRef(stringifyEntityRef(template));
      navigate(templateRoute({ namespace, templateName: name }));
    },
    [navigate, templateRoute],
  );

  const { allowed: canViewTasks } = usePermission({ permission: taskReadPermission });

  return (
    <EntityListProvider>
      <Page themeId="home">
        <Header
          pageTitleOverride={t('templateListPage.pageTitle')}
          title={t('templateListPage.title')}
          subtitle={t('templateListPage.subtitle')}
          {...headerOptions}
        >
          <ScaffolderPageContextMenu {...scaffolderPageContextMenuProps} />
        </Header>

        <Content>
          <ContentHeader>
            <RegisterExistingButton
              title={t('templateListPage.contentHeader.registerExistingButtonTitle')}
              to={registerComponentRoute?.()}
            />
            {canViewTasks ? (
              <Box ml={1}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AssignmentIcon />}
                  onClick={() => navigate(tasksLink())}
                  data-testid="my-tasks-button"
                >
                  My Tasks
                </Button>
              </Box>
            ) : null}
            <SupportButton>
              {t('templateListPage.contentHeader.supportButtonTitle')}
            </SupportButton>
          </ContentHeader>

          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters>
              <EntitySearchBar />
              <EntityKindPicker initialFilter="template" hidden />
              <UserListPicker initialFilter="all" availableFilters={['all', 'starred']} />
              <TemplateCategoryPicker />
              <EntityTagPicker />
              <EntityOwnerPicker />
            </CatalogFilterLayout.Filters>
            <CatalogFilterLayout.Content>
              <TemplateGroups
                groups={groups}
                templateFilter={templateFilter}
                TemplateCardComponent={TemplateCardComponent}
                onTemplateSelected={onTemplateSelected}
                additionalLinksForEntity={additionalLinksForEntity}
              />
            </CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </Content>
      </Page>
    </EntityListProvider>
  );
};

export default TemplateListPageWithTasks;

